import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Save, ShieldCheck } from "lucide-react";
import AppHeader from "@/components/layout/AppHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminSettingsPage() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [ttl, setTtl] = useState(7);
  const [initialTtl, setInitialTtl] = useState(7);
  const [saving, setSaving] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);

  useEffect(() => {
    if (!loading && user && user.role !== "owner") {
      nav("/app", { replace: true });
    }
  }, [loading, user, nav]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/account/settings");
        if (!alive) return;
        setTtl(data.share_token_ttl_days);
        setInitialTtl(data.share_token_ttl_days);
      } catch (e) {
        toast.error(e.response?.data?.detail || "No se pudieron cargar los ajustes");
      } finally {
        if (alive) setLoadingSettings(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const save = async () => {
    const n = parseInt(ttl, 10);
    if (!Number.isFinite(n) || n < 1 || n > 365) {
      toast.error("La caducidad debe estar entre 1 y 365 días");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/account/settings", { share_token_ttl_days: n });
      setTtl(data.share_token_ttl_days);
      setInitialTtl(data.share_token_ttl_days);
      toast.success(`Caducidad guardada: ${data.share_token_ttl_days} día${data.share_token_ttl_days === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const dirty = parseInt(ttl, 10) !== initialTtl;

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-ajustes-page">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EDF5F3] text-[#1F4640] text-xs font-bold uppercase tracking-widest">
          <ShieldCheck className="w-3.5 h-3.5" /> Panel Propietario
        </div>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="admin-ajustes-title">
          Ajustes de la cuenta
        </h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          Controla cuánto tiempo viven los enlaces públicos que compartes por WhatsApp.
          El cambio afecta solo a los enlaces que generes a partir de hoy; los que ya enviaste conservan su caducidad.
        </p>

        <section className="mt-8 rounded-2xl bg-white border border-[#E2DCD2] p-6">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#FFF7EE] flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-[#92400E]" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-lg font-bold text-slate-900">
                Caducidad de los enlaces compartidos
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Después de este plazo, el link deja de abrir el PDF o Word.
                El cliente verá un mensaje para pedir uno nuevo. También puedes revocar un link
                manualmente desde la pantalla del documento.
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <label htmlFor="ttl-input" className="text-sm font-semibold text-slate-700">
                  Días:
                </label>
                <input
                  id="ttl-input"
                  type="number"
                  min={1}
                  max={365}
                  value={ttl}
                  onChange={(e) => setTtl(e.target.value)}
                  disabled={loadingSettings || saving}
                  data-testid="ttl-input"
                  className="w-28 h-11 px-3 rounded-xl border border-[#CBD5E1] bg-white outline-none focus:ring-2 focus:ring-[#1E3A5F] text-slate-900 font-semibold"
                />
                <span className="text-xs text-slate-500">(entre 1 y 365)</span>
                <button
                  onClick={save}
                  disabled={!dirty || saving || loadingSettings}
                  data-testid="save-settings-btn"
                  className="bm-btn-primary h-11 px-5 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? "Guardando…" : "Guardar"}
                </button>
              </div>

              <div className="mt-4 text-xs text-slate-500">
                Default seguro: 7 días. Puedes bajarlo a 1 día si estás probando el flujo
                desde celular y quieres ver la expiración rápido.
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
